import { Avatar, Box, Grid, Paper, Switch, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { OrganizationAPI, RoleAPI } from "../../api";
import OrganizationScopedPage from "../../components/OrganizationScopedPage";
import { PageContainer } from "../../components/layout";
import { PageHeader } from "../../components/layout/page";
import useFeature from "../../hooks/useFeature";
import { useGlobalOrganizationContext } from "../../hooks/useGlobalOrganizationContext";
import { useGlobalUserContext } from "../../hooks/useGlobalUserContext";
import useIsAdmin from "../../hooks/useIsAdmin";
import { useAlert } from "../../lib/alert";
import { Role, User } from "../../model";
import { ContactLabels, ContactType } from "../../model/Contact";
import ManageContactInfo from "./components/ManageContactInfo";
import RoleAutocompleteComponent from "./components/RoleAutocompleteComponent";

const MemberManagementPage: FC<unknown> = () => {
  const { memberId } = useParams();
  const globalUser = useGlobalUserContext();
  const organization = useGlobalOrganizationContext().organization;
  const alert = useAlert();
  const isAdmin = useIsAdmin();
  const [loaded, setLoaded] = useState(false);
  const [user, setUser] = useState<User | undefined>();
  const [orgRoles, setOrgRoles] = useState<Role[]>([]);
  const [visible, setVisible] = useState(false);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const visibilityToggleEnabled = useFeature("members.visibilityToggle");

  const getLabelFromRoles = (roles: Role[]) => {
    let str = "";

    roles.map((role, index) => {
      str += role.name;
      if (index < roles.length - 1) {
        str += ", ";
      }
    });

    return str;
  };

  useEffect(() => {
    setLoaded(false);
    if (memberId && organization) {
      Promise.all([
        RoleAPI.byOrganization(organization?.id),
        OrganizationAPI.getMembers(organization?.id),
        OrganizationAPI.getVisibility(organization.id ?? "", memberId ?? ""),
      ])
        .then((res) => {
          const [roles, members, vis] = res;

          const member = members.find((m) => m.id === memberId);
          const memberRoles = member?.roles;

          // Can edit self
          if (member?.id === globalUser.user.id) {
            setCanEdit(true);
          }

          // Admins can edit others
          if (!canEdit && isAdmin) {
            setCanEdit(true);
          }

          setUser(member);
          setUserRoles(memberRoles ?? []);
          setVisible(vis);
          setOrgRoles(roles);
          setLoaded(true);
        })
        .catch((err) => {
          console.error("Error loading member data", err);
          alert.error("Error loading member data. Please try again later.");
          setLoaded(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, organization]);

  if (!loaded) {
    return (
      <OrganizationScopedPage
        displayOrganizationActionMenu={true}
        category={"members"}
      >
        <PageHeader title={"Manage Member"} />
        <PageContainer>
          <Grid container spacing={3}>
            {/* Profile Section Skeleton */}
            <Grid size={12}>
              <Paper
                elevation={3}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  padding: 3,
                  borderRadius: 3,
                  boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    marginRight: 3,
                    backgroundColor: grey[300],
                    borderRadius: "50%",
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      width: "50%",
                      height: 24,
                      backgroundColor: grey[300],
                      mb: 1,
                      borderRadius: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: "30%",
                      height: 20,
                      backgroundColor: grey[300],
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Contact Info Section Skeleton */}
            <Grid size={12}>
              <Typography variant="h6">Contact Information</Typography>
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 3,
                  padding: 2,
                  boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 20,
                    backgroundColor: grey[300],
                    mb: 2,
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    width: "100%",
                    height: 40,
                    backgroundColor: grey[300],
                    borderRadius: 1,
                  }}
                />
              </Paper>
            </Grid>

            {/* Roles Section Skeleton */}
            <Grid size={12}>
              <Typography variant="h6">Roles</Typography>
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 3,
                  padding: 2,
                  boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 20,
                    backgroundColor: grey[300],
                    mb: 2,
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    width: "100%",
                    height: 40,
                    backgroundColor: grey[300],
                    borderRadius: 1,
                  }}
                />
              </Paper>
            </Grid>

            {/* Directory Visibility Section Skeleton */}
            <Grid size={12}>
              <Typography variant="h6">Public Directory</Typography>
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 3,
                  padding: 3,
                  boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 20,
                    backgroundColor: grey[300],
                    mb: 2,
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    width: "100%",
                    height: 40,
                    backgroundColor: grey[300],
                    borderRadius: 1,
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </PageContainer>
      </OrganizationScopedPage>
    );
  }

  if (organization && user && memberId)
    return (
      <OrganizationScopedPage
        displayOrganizationActionMenu={true}
        category={"members"}
      >
        <PageHeader title={"Manage Member"} />
        <PageContainer>
          <Grid container spacing={3}>
            {/* Profile Section */}
            <Grid size={12}>
              <Paper
                elevation={3}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  padding: 3,
                  borderRadius: 3,
                  boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)", // Lighter and smaller shadow
                  backgroundColor: "#f9f9f9", // Same color as other Paper components
                }}
              >
                <Avatar
                  src={user?.profile_picture}
                  sx={{ width: 100, height: 100, marginRight: 3 }}
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h5">
                    {user
                      ? `${user.first_name} ${user.last_name}`
                      : "Loading..."}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {organization?.name || "Loading..."}
                  </Typography>
                  <Box sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {getLabelFromRoles(userRoles) || "Member"}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Contact Info Section */}
            <Grid size={12}>
              <Typography variant="h6">Contact Information</Typography>
              {organization?.id && (
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    padding: 2,
                    boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    {canEdit
                      ? `Manage ${user.first_name}'s contact information,
                    including their primary email and phone number. This
                    information is used for communication and identification.`
                      : `You can view ${user.first_name}'s primary contact information below:`}
                  </Typography>
                  <Grid
                    container
                    spacing={0}
                    direction="column"
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                    size={12}
                  >
                    <Grid
                      sx={{
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <ManageContactInfo
                        organization={organization}
                        userId={memberId}
                        type={ContactType.EMAIL}
                        label={ContactLabels.PRIMARY}
                        disabled={!canEdit}
                        hideLabelText
                      />
                    </Grid>
                    <Grid sx={{ backgroundColor: "#f9f9f9" }} size={12}>
                      <ManageContactInfo
                        organization={organization}
                        userId={memberId}
                        type={ContactType.PHONE}
                        label={ContactLabels.PRIMARY}
                        disabled={!canEdit}
                        hideLabelText
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Grid>

            {/* Roles Section */}
            {isAdmin && (
              <Grid size={12}>
                <Typography variant="h6">Roles</Typography>
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    padding: 2,
                    boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    Assign roles to {user.first_name} to define their
                    permissions and responsibilities within the organization.
                    Roles determine what resources they can access and their
                    position within the organization.
                  </Typography>
                  <RoleAutocompleteComponent
                    userId={memberId}
                    organizationId={organization.id}
                    isInvitation={false}
                    organizationRoles={orgRoles}
                    assignedRoles={userRoles}
                  />
                </Paper>
              </Grid>
            )}

            {/* Directory Visibility */}
            {visibilityToggleEnabled && isAdmin && (
              <Grid size={12}>
                <Typography variant="h6">Public Directory</Typography>
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    padding: 3,
                    boxShadow: "0px 2px 0px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: "bold", color: grey[800] }}>
                        Directory Visibility
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                      >
                        Control whether {user.first_name}&apos;s contact
                        information is visible to others in the public
                        directory.
                      </Typography>
                    </Box>
                    <Box>
                      <Switch
                        checked={visible}
                        onChange={() => {
                          OrganizationAPI.setVisibility(
                            organization?.id ?? "",
                            memberId ?? "",
                            !visible,
                          ).then(() => {
                            setVisible(!visible);
                          });
                        }}
                        color="primary"
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: visible ? "green" : "red",
                          textAlign: "center",
                          mt: 0.5,
                        }}
                      >
                        {visible ? "Visible" : "Not Visible"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </PageContainer>
      </OrganizationScopedPage>
    );
};

export default MemberManagementPage;
